/** @jsxImportSource @emotion/react */
'use memo'

import { useEffect, useId, useRef, useState } from 'react'
import { SearchOutlined } from '@ant-design/icons'
import styled from '@emotion/styled'
import { Input } from 'antd'
import colors from '@sportninja/common/constants/appColors'
import { useDebounce } from '@sportninja/common/hooks/useDebounce'
import { paragraphSmall, weightRegular } from 'src/components/css'
import Text from 'src/components/ts-components/Text'

interface FilterSearchInputProps {
  /** The search currently applied — the box follows it when it changes from outside. */
  value: string
  /** Called with the text once typing has settled. */
  onSearch: (value: string) => void
  placeholder: string
  /**
   * A label above the box, set like the labels of the selects beside it.
   * Without one the placeholder names the box for assistive tech.
   */
  label?: string
  /** How long typing must pause before `onSearch` fires. */
  debounceMs?: number
  /** The box's width in px on desktop; full width on mobile either way. */
  width?: number
}

/** The schedule Registration tab's search width. */
const DEFAULT_WIDTH = 320

/**
 * The free-text search box of a filter row — the schedule Registration tab's
 * search, shared so every filter row searches with the same control.
 *
 * Controlled from outside: `value` is the applied search (from context, or
 * the URL), so a shared link opens with its search already in the box, and
 * Back or a cleared filter empties it. Typing reports through `onSearch`
 * after a pause rather than on every keystroke.
 */
const FilterSearchInput = ({
  value,
  onSearch,
  placeholder,
  label,
  debounceMs = 300,
  width = DEFAULT_WIDTH,
}: FilterSearchInputProps) => {
  'use memo'

  const inputId = useId()

  const [text, setText] = useState(value)
  const settledText = useDebounce(text, debounceMs)
  // The last value reported or received, so neither effect echoes the other.
  const lastValueRef = useRef(value)

  useEffect(() => {
    if (settledText !== lastValueRef.current) {
      lastValueRef.current = settledText
      onSearch(settledText)
    }
  }, [settledText, onSearch])

  useEffect(() => {
    if (value !== lastValueRef.current) {
      lastValueRef.current = value
      setText(value)
    }
  }, [value])

  const box = (
    <SearchBox
      id={inputId}
      placeholder={placeholder}
      aria-label={label ? undefined : placeholder}
      prefix={<SearchIcon />}
      value={text}
      onChange={(event) => setText(event.target.value)}
      allowClear
      boxWidth={width}
    />
  )

  if (!label) {
    return box
  }

  return (
    <Field>
      <label htmlFor={inputId}>
        <Text variant='paragraphSmall' weight='regular' color={colors.WHITE}>
          {label}
        </Text>
      </label>
      {box}
    </Field>
  )
}

export default FilterSearchInput

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`

// `boxWidth` is styling only — kept off antd's Input, which would pass it to the DOM.
const SearchBox = styled(Input, {
  shouldForwardProp: (prop) => prop !== 'boxWidth',
})<{ boxWidth: number }>`
  ${paragraphSmall}
  ${weightRegular}
  width: ${({ boxWidth }) => boxWidth}px;
  height: 40px !important;
  border-radius: 8px;
  border: 1px solid ${colors.SECONDARY_100};
  background: ${colors.SECONDARY_200};
  color: ${colors.WHITE};

  @media (max-width: 768px) {
    width: 100%;
  }

  .ant-input {
    background-color: ${colors.SECONDARY_500};
    color: ${colors.WHITE};
  }

  .ant-input-prefix {
    color: ${colors.NEUTRAL_100};
  }

  &::placeholder {
    color: ${colors.NEUTRAL_100};
  }
`

const SearchIcon = styled(SearchOutlined)`
  font-size: 16px;
  color: ${colors.NEUTRAL_50};
`
